import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Loader2,
  RefreshCw,
  Send,
  MessageCircle,
  X,
} from "lucide-react";

// V1.55 · Botón de chat de Kai — temporalmente en fase de pruebas.
function KaiChatButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="kai-chat-button"
        className="fixed bottom-5 right-5 z-40 h-13 w-auto px-4 py-3 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white shadow-lg flex items-center gap-2 text-sm font-semibold hover:scale-105 transition-transform"
      >
        <MessageCircle className="h-5 w-5" /> Chat con Kai
      </button>
      {open && (
        <div className="fixed bottom-20 right-5 z-40 w-72 rounded-2xl bg-white border border-neutral-200 shadow-xl p-4" data-testid="kai-chat-popup">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white flex items-center justify-center font-bold text-xs">K</div>
              <div className="text-sm font-bold">Kai</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700" data-testid="kai-chat-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
            ¡Hola! Soy Kai 👋 Aunque temporalmente estoy en fase de pruebas,
            si tienes cualquier duda escríbenos por WhatsApp y la autoescuela
            te ayudará al momento.
          </p>
        </div>
      )}
    </>
  );
}

// V1.55 · Página pública del alumno — todos los documentos en serie en una
// misma página, guiados por Kai, con revisión final verde/rojo.

function KaiBubble({ children }) {
  return (
    <div className="flex items-start gap-2.5" data-testid="kai-bubble">
      <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white flex items-center justify-center font-bold text-sm shadow">
        K
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white border border-neutral-200 shadow-sm px-4 py-3 text-sm text-neutral-800 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

const ITEM_STATUS = {
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  completed: { label: "Completado", cls: "bg-green-100 text-green-800 border-green-200" },
  expired: { label: "Caducado", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  cancelled: { label: "Cancelado", cls: "bg-red-100 text-red-700 border-red-200" },
};

export default function DocumentBundlePage() {
  useDocumentTitle("Tus documentos · Natur Autoescuela");
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(null); // {status, missing}
  const pollRef = useRef(null);

  const fetchBundle = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/public/doc-bundle/${token}`);
      setData(res);
      setError(null);
      return res;
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo cargar el enlace.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBundle();
    pollRef.current = setInterval(fetchBundle, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchBundle]);

  const startReview = async () => {
    setReviewing(true);
    setReviewDone(false);
    await new Promise((r) => setTimeout(r, 2500));
    await fetchBundle();
    setReviewDone(true);
  };

  const confirmSend = async () => {
    setFinishing(true);
    try {
      const { data: res } = await api.post(`/public/doc-bundle/${token}/finish`, { send_anyway: true });
      setFinished(res);
      clearInterval(pollRef.current);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo confirmar.");
    } finally {
      setFinishing(false);
    }
  };

  const items = data?.items || [];
  const pendingItems = items.filter((i) => i.status !== "completed");
  const isActive = data?.status === "pending";

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md space-y-4" data-testid="doc-bundle-page">
        <div className="flex flex-col items-center">
          <Logo variant="card" />
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mt-3">
            Tus documentos
          </div>
        </div>

        {loading && (
          <p className="text-center text-neutral-500 py-8" data-testid="bundle-loading">Cargando…</p>
        )}

        {!loading && error && (
          <div className="card-surface p-6 text-center" data-testid="bundle-error">
            <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-2" />
            <div className="font-bold">No se pudo cargar</div>
            <p className="text-sm text-neutral-500 mt-1">{String(error)}</p>
          </div>
        )}

        {!loading && !error && data && (data.status === "expired" || data.status === "cancelled") && !finished && (
          <div className="card-surface p-6 text-center" data-testid="bundle-inactive">
            <Clock className="h-10 w-10 text-neutral-400 mx-auto mb-2" />
            <div className="font-bold">Este enlace ya no está activo</div>
            <p className="text-sm text-neutral-500 mt-1">
              Por seguridad los enlaces son temporales. No te preocupes: pídenos otro y te lo enviaremos inmediatamente.
            </p>
          </div>
        )}

        {!loading && !error && data && (data.status === "completed" || data.status === "completed_with_missing" || finished) && (
          <div className="card-surface p-6 text-center" data-testid="bundle-finished">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-green-800">Documentos enviados a Natur</div>
            <p className="text-sm text-neutral-600 mt-1">
              ¡Gracias{data.student_first_name ? `, ${data.student_first_name}` : ""}! Hemos recibido tu confirmación.
            </p>
            {(finished?.missing?.length > 0 || data.status === "completed_with_missing") && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
                Quedó algo pendiente — la autoescuela se pondrá en contacto contigo para completarlo.
              </p>
            )}
          </div>
        )}

        {!loading && !error && data && isActive && !finished && (
          <>
            <KaiBubble>
              ¡Hola{data.student_first_name ? `, ${data.student_first_name}` : ""}! 👋 Soy <strong>Kai</strong>, tu asistente virtual.
              Te voy a guiar paso a paso: por ejemplo, lo primero que vamos a hacer es firmar el contrato,
              y poco a poco iremos cumplimentando el resto. Al finalizar revisaremos juntos que todo esté correcto.
              <br /><br />
              Recuerda que <strong>el enlace por seguridad es temporal</strong>. Pero no te preocupes: si no te da
              tiempo, solicítanos otro y te lo enviaremos inmediatamente.
            </KaiBubble>

            <div className="space-y-2.5">
              {items.map((it, idx) => {
                const meta = ITEM_STATUS[it.status] || ITEM_STATUS.pending;
                const done = it.status === "completed";
                return (
                  <div key={it.kind} className="card-surface p-3.5 flex items-center gap-3" data-testid={`bundle-item-${it.kind}`}>
                    <div className={"h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold " + (done ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500")}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-neutral-800 truncate">{it.label}</div>
                      <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border " + meta.cls}>
                        {meta.label}
                      </span>
                    </div>
                    {!done && it.status === "pending" && (
                      <a href={it.url_path} target="_blank" rel="noreferrer" data-testid={`bundle-open-${it.kind}`}
                        className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-colors">
                        Abrir <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>

            <button type="button" onClick={fetchBundle} data-testid="bundle-refresh"
              className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg text-xs font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Actualizar estado
            </button>

            {!reviewing && (
              <button type="button" onClick={startReview} data-testid="bundle-review-btn"
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-semibold bg-neutral-900 hover:bg-neutral-800 text-white transition-colors">
                Revisar y enviar todo a Natur
              </button>
            )}

            {reviewing && !reviewDone && (
              <KaiBubble>
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                  Estoy revisando los documentos… esto tardará unos segundos, no cierres la ventana…
                </span>
              </KaiBubble>
            )}

            {reviewing && reviewDone && (
              <div className="space-y-3" data-testid="bundle-review-result">
                <div className="card-surface p-4 space-y-2">
                  {items.map((it) => (
                    <div key={it.kind} className="flex items-center gap-2 text-sm" data-testid={`review-check-${it.kind}`}>
                      {it.status === "completed"
                        ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                        : <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
                      <span className={it.status === "completed" ? "text-neutral-800" : "text-red-700 font-semibold"}>
                        {it.label}
                      </span>
                    </div>
                  ))}
                </div>

                {pendingItems.length === 0 ? (
                  <>
                    <KaiBubble>
                      ¡Listo! ✨ Está todo perfecto. Por favor, pulsa en <strong>Confirmar</strong> para enviar
                      todos los documentos a Natur.
                    </KaiBubble>
                    <button type="button" onClick={confirmSend} disabled={finishing} data-testid="bundle-confirm-btn"
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl text-sm font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white transition-colors">
                      <Send className="h-4 w-4" /> {finishing ? "Enviando…" : "Confirmar y enviar a Natur"}
                    </button>
                  </>
                ) : (
                  <>
                    <KaiBubble>
                      He detectado que aún falta algo por completar (en rojo). Puedes terminarlo ahora,
                      o si lo prefieres, enviar lo que tienes de todos modos.
                    </KaiBubble>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setReviewing(false); setReviewDone(false); }}
                        data-testid="bundle-continue-btn" className="btn-secondary flex-1">
                        Seguir completando
                      </button>
                      <button type="button" onClick={confirmSend} disabled={finishing} data-testid="bundle-send-anyway-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-semibold bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white transition-colors">
                        {finishing ? "Enviando…" : "Enviar de todos modos"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {!loading && !error && data && isActive && !finished && <KaiChatButton />}
      <Footer className="mt-4" />
    </div>
  );
}
